import React from 'react';

// Root error boundary — without one, ANY uncaught render error unmounts the
// entire tree: white screen, no nav, no way back. The most common trigger is
// a lazy-chunk 404: a tab/PWA left open across a Vercel redeploy asks for a
// chunk file that no longer exists the first time someone opens that tab
// (this is how the Inventory page "lost its menu" on 2026-08-12).
//
// Chunk-load errors are transient — a reload fetches the new build — so those
// auto-reload once per session; everything else gets a reload card instead of
// a blank page.
const RELOAD_FLAG = 'cp-chunk-reloaded';

function isChunkLoadError(error) {
  return error?.name === 'ChunkLoadError'
    || /Loading (CSS )?chunk .* failed/i.test(error?.message || '');
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error('[APP ERROR BOUNDARY]', error);
    if (isChunkLoadError(error)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1';
        if (!alreadyReloaded) sessionStorage.setItem(RELOAD_FLAG, '1');
      } catch {}
      if (!alreadyReloaded) window.location.reload();
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    // Chunk errors auto-reload above; this card only shows for repeat chunk
    // failures or non-chunk render errors.
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F9FAFB',
        padding: 24,
        fontFamily: 'Inter, sans-serif',
      }}>
        <div style={{
          maxWidth: 360,
          width: '100%',
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>😵</div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111827', margin: '0 0 8px' }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>
            The app hit an error{isChunkLoadError(this.state.error) ? ' loading an updated page' : ''}. Reloading usually fixes it.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '10px 0',
              border: 'none',
              borderRadius: 8,
              background: '#F07EA0',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
